import { useEffect, useState, useContext, useMemo, useReducer, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { isEqual, merge } from 'lodash';

import { trans, restrictCard } from '@mssgme/shared';
import { useWorkspace } from '@mssgme/hooks';
import { PreviewContext } from './context';

const loadLocales = async (lang = 'en', namespace) => {
    const { default: locales } = await import(
        /* webpackInclude: /\.json$/ */
        /* webpackChunkName: "locale." */
        `@mssgme/locales/${lang}/${namespace}.json`
    );

    return locales;
};

export function useLocales(namespace) {
    const { i18n } = useTranslation('cards');
    const [isLoaded, setLoaded] = useState(false);

    useEffect(() => {
        async function load() {
            try {
                const locales = await loadLocales(i18n.language, namespace);
                trans.extend(locales);
            } finally {
                setLoaded(true);
            }
        }

        void load();
    }, [i18n.language]);

    return { isLoaded, i18n };
}

function previewReducer(state, action) {
    switch (action.type) {
        case 'UPDATE_DRAFT':
            return merge({}, state, { draft: action.payload });
        case 'UPDATE_INITIAL':
            return { initial: action.payload, draft: action.payload };
        case 'RESET_DRAFT':
            return { ...state, draft: state.initial };
        default:
            throw new Error(`"${action.type}" is invalid action`);
    }
}

export function usePreviewStore() {
    const workspace = useWorkspace();
    const location = useLocation();
    const collections = useSelector((state) => state.collections);
    const { card, id: workspaceId } = workspace;
    const initialState = useMemo(() => ({ ...card, workspaceId, collections }), [card, workspaceId, collections]);
    const [state, dispatch] = useReducer(previewReducer, { initial: initialState, draft: initialState });

    const updateDraft = useCallback((payload) => dispatch({ type: 'UPDATE_DRAFT', payload }), [dispatch]);
    const updateInitial = useCallback((payload) => dispatch({ type: 'UPDATE_INITIAL', payload }), [dispatch]);
    const resetDraft = useCallback(() => dispatch({ type: 'RESET_DRAFT' }), [dispatch]);

    const dirty = useMemo(() => !isEqual(state.draft, state.initial), [state.draft, state.initial]);

    useEffect(() => {
        updateInitial(initialState);
    }, [initialState]);

    // reset only when changed location
    useEffect(() => {
        if (dirty && location) {
            resetDraft();
        }
    }, [location]);

    const preview = useMemo(() => restrictCard(state.draft, workspace), [state.draft, workspace]);

    return {
        preview,
        updateDraft,
        updateInitial,
        resetDraft,
    };
}

export function usePreview() {
    const { preview, updateDraft, resetDraft } = useContext(PreviewContext);

    return { preview, updatePreview: updateDraft, resetPreview: resetDraft };
}
